'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

const FEATURED_YACHTS = [
  { name: '72m Motor Yacht — Mediterranean', location: 'Mediterranean', desc: '12 guests, 20 crew. The ultimate Mediterranean summer charter.', price: 'From $750,000 per week', tags: ['Motor Yacht', 'Mediterranean', 'Ultra-luxury'] },
  { name: 'Indonesia Phinisi Charter', location: 'Indonesia', desc: '8 guests, traditional Phinisi design. Komodo, Raja Ampat, Banda Sea.', price: 'From $68,000 per week', tags: ['Sailing', 'Indonesia', 'Traditional'] },
  { name: '40m Motor Yacht — Galápagos', location: 'Galápagos', desc: '12 guests, endemic wildlife encounters. Exclusive Galápagos charter.', price: 'From $220,000 per week', tags: ['Galápagos', 'Wildlife', 'Expedition'] },
  { name: '60m Sailing Yacht — Caribbean', location: 'Caribbean', desc: '10 guests, luxury sailing. BVI, St Barths, Grenadines, Antigua.', price: 'From $320,000 per week', tags: ['Sailing', 'Caribbean', 'Luxury'] },
];

const FEATURES = [
  { icon: '🔍', title: 'Vetted fleet', desc: 'Only crewed, professionally maintained yachts inspected annually.' },
  { icon: '👨‍🍳', title: 'Chef & toys included', desc: 'Onboard chef, tenders, jet-skis, paddleboards, dive kit.' },
  { icon: '🗺', title: 'Bespoke itineraries', desc: 'Your captain crafts the route around wind, weather and your interests.' },
];

const DESTINATIONS = [
  { name: 'Mediterranean', icon: '🌊', season: 'May–October', desc: 'Amalfi, Greek Islands, Croatia, French Riviera' },
  { name: 'Caribbean', icon: '🏝', season: 'November–April', desc: 'BVI, St Barths, Grenadines, Antigua' },
  { name: 'Galápagos', icon: '🐢', season: 'Year-round', desc: 'Endemic wildlife, volcanic landscapes' },
  { name: 'Indonesia', icon: '🇮🇩', season: 'April–October', desc: 'Komodo, Raja Ampat, Banda Sea' },
  { name: 'South Pacific', icon: '🌺', season: 'May–October', desc: 'French Polynesia, Fiji, Tonga' },
  { name: 'Indian Ocean', icon: '🐠', season: 'November–April', desc: 'Seychelles, Maldives, Mozambique' },
];

export default function YachtsPage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/villas" className="hover:text-[var(--primary)] transition-colors">Villas</Link>
          <Link href="/cruises" className="hover:text-[var(--primary)] transition-colors">Cruises</Link>
          <Link href="/private-jets" className="hover:text-[var(--primary)] transition-colors">Private Jets</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">The blue horizon</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Yacht Charters<br />
            <span className="gold-text-gradient">Your Ocean, Your Rules.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-6 max-w-xl">
            Motor and sailing yacht charters worldwide — Mediterranean summers, Caribbean winters, the Galápagos, Indonesia and the South Pacific — with full crew, chef and toys.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {FEATURES?.map((f) => (
              <div key={f?.title} className="glass-card rounded-xl p-4 border border-[rgba(201,168,76,0.1)]">
                <div className="text-2xl mb-2">{f?.icon}</div>
                <div className="text-sm font-bold mb-1">{f?.title}</div>
                <div className="text-xs text-[var(--muted-foreground)]">{f?.desc}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <button onClick={() => router?.push('/booking?vertical=yacht')} className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">Design Your Voyage</button>
            <Link href="/ai-concierge" className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)]">Ask Concierge</Link>
          </div>
        </div>
      </section>
      {/* Featured Yachts */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Featured Yachts</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Handpicked <span className="gold-text-gradient">Charter Yachts</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FEATURED_YACHTS?.map((y) => (
              <div key={y?.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer" onClick={() => router?.push('/booking?vertical=yacht')}>
                <div className="text-2xl mb-3">⛵</div>
                <h3 className="text-base font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{y?.name}</h3>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{y?.location}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-3 leading-relaxed">{y?.desc}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {y?.tags?.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)]">{tag}</span>
                  ))}
                </div>
                <p className="text-xs font-semibold text-[var(--primary)]">{y?.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Destinations */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Charter Destinations</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Where to <span className="gold-text-gradient">Set Sail</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {DESTINATIONS?.map((d) => (
              <button key={d?.name} onClick={() => router?.push(`/booking?vertical=yacht&destination=${d?.name}`)} className="glass-card rounded-xl p-4 hover:border-[rgba(201,168,76,0.3)] transition-all group text-left">
                <div className="text-2xl mb-2">{d?.icon}</div>
                <div className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors">{d?.name}</div>
                <div className="text-[10px] text-[var(--primary)] mb-1">{d?.season}</div>
                <div className="text-[10px] text-[var(--muted-foreground)]">{d?.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-6">FAQ</p>
          <div className="space-y-4">
            {[
              { q: 'What is APA?', a: 'Advance Provisioning Allowance covers food, fuel and dockage — reconciled at trip end. Typically 25–35% of the charter fee.' },
              { q: 'What is included in the charter fee?', a: 'The yacht, full crew, and all onboard amenities. APA covers running costs. We\'ll provide a full breakdown before booking.' },
              { q: 'How far in advance should I book?', a: 'Peak season (Mediterranean July–August, Caribbean Christmas) books 12–18 months ahead. Off-peak can often be arranged in 4–6 weeks.' },
            ]?.map((faq) => (
              <div key={faq?.q} className="glass-card rounded-xl p-5">
                <div className="text-sm font-bold mb-2 text-[var(--primary)]">{faq?.q}</div>
                <div className="text-xs text-[var(--muted-foreground)] leading-relaxed">{faq?.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/villas" className="hover:text-[var(--primary)] transition-colors">Villas</Link>
            <Link href="/cruises" className="hover:text-[var(--primary)] transition-colors">Cruises</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
